// Imports for external libraries go here.
import React, { FC, useEffect, useState, useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { Icon, Types, Text, Messages, RichText } from '@marriott/mi-ui-library';
import { useQuery } from '@apollo/client';
import { getUserDetails, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { USER_STATE_AUTHENTICATED, constants } from '../../modules';
import { generateApolloClientHeaders } from '../../modules';
import { phoenixAccountDttGetCustomerRewardsCount } from '../../modules/graphV2/index';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { replaceText } from '../../modules/utils/promotionHelper';
import { PageContext } from '../../modules/context/PageContext';
import { SkeletonLoaderV2 } from '../../molecules';
import { AccountMetricsProps, MetricsDetails, UserAwards, UserAwardsDtt } from './AccountMetrics.types';
import { StyledAccountMetrics } from './AccountMetrics.styles';

// Use named rather than default exports.
export const AccountMetrics: FC<AccountMetricsProps> = props => {
  const [points, setPoints] = useState<string | undefined>();
  const [awards, setAwards] = useState<string | undefined>();
  const isAuthorMode = props?.isAuthorMode;
  const pageContext = useContext(PageContext);
  const { userProfileSummary } = useUserDetailsGlobalStore(state => state);
  const { userAwards, setUserAwards } = useAccountPersistentStore(state => state);
  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const userState = pageContext?.userState;
  const dataLoaded = useRef<boolean>(false);

  useEffect(() => {
    if (userState === USER_STATE_AUTHENTICATED) {
      if (!userProfileSummary) {
        getUserDetails(true);
      } else {
        setPoints(userProfileSummary?.currentPoints?.toString());
      }
    }
  }, [userProfileSummary]);

  useEffect(() => {
    if (userAwards !== undefined) {
      setAwards(userAwards?.toString());
    }
  }, [userAwards]);

  // Memoize all variables that affect the query,
  // to prevent re-triggering useQuery if component re-renders.
  const skipQuery =
    useMemo(() => {
      return !isAuthorMode && !pageContext.sessionData;
    }, [pageContext, isAuthorMode]) ||
    dataLoaded.current ||
    isAuthorMode ||
    userState !== USER_STATE_AUTHENTICATED ||
    (userAwards !== undefined && userState === USER_STATE_AUTHENTICATED);

  // ***********UXL***********
  const { loading: awardsLoading, error: awardsError } = useQuery(phoenixAccountDttGetCustomerRewardsCount, {
    variables: {
      customerId: sessionData?.consumerID,
    },
    context: generateApolloClientHeaders(false, pageContext),
    skip: skipQuery,
    onCompleted: (data: UserAwards | UserAwardsDtt) => {
      dataLoaded.current = true;
      const freeNights =
        (data?.customer?.loyaltyInformation?.certificates && data?.customer?.loyaltyInformation?.certificates?.total) ||
        0;

      const suiteNights =
        (data as UserAwardsDtt)?.customer?.loyaltyInformation?.nightlyUpgradeAwards?.available?.count ?? 0;

      setUserAwards(Number(suiteNights) + Number(freeNights));
    },
  });

  const getDescription = (label: string, key: string) => {
    const description =
      key === constants.PARTNER_CURRENCY.toLowerCase()
        ? replaceText(label || '', [points?.toString() || ''])
        : replaceText(label || '', [awards?.toString() || '']);

    return (
      <Text
        fontSize={Types.size.extraSmall}
        element={Types.tags.span}
        copyText={description}
        customClass="accountmetrics__heading t-body-xs align-self-center"
      />
    );
  };

  return userState === USER_STATE_AUTHENTICATED ? (
    <StyledAccountMetrics
      data-testid="account-AccountMetrics"
      data-component-name="o-account-AccountMetrics"
      className={clsx('pt-4', props?.styleClass)}
    >
      <div className="accountmetrics accountmetrics__container container d-flex justify-content-start align-items-center">
        {points === undefined && awardsError ? (
          <div className="container" data-testid="uxl-error">
            <Messages messageType="error-sev1">
              <RichText text={pageContext?.uxlErrorMessage} componentId="uxl-error-msg" />
            </Messages>
          </div>
        ) : awardsLoading ? (
          <SkeletonLoaderV2 className="accountmetrics__loader" />
        ) : (
          props?.metricsDetails?.map((item: MetricsDetails, index: number) => {
            const isAwardsWithError = item?.key === constants.AWARDS && awardsError;
            if (isAwardsWithError) return null;
            return (
              <>
                <div className="accountmetrics__points d-flex justify-content-between align-items-start">
                  <Icon
                    iconClass={`${item?.icon} icon-s icon-decorative ${
                      item?.key === constants.AWARDS && 'accountmetrics__icon'
                    }`}
                  />
                  {getDescription(item?.label, item?.key)}
                </div>
                {index !== props?.metricsDetails?.length - 1 && <div className="accountmetrics__separator"></div>}
              </>
            );
          })
        )}
      </div>
    </StyledAccountMetrics>
  ) : null;
};
